/* Sudsy FAQ page behaviour: search filtering and help-card modals.
   Everything is rendered up front, so search is a plain client-side filter
   over the questions already in the DOM — no index, no fetching. */
(function () {
  'use strict';

  function normalize(value) {
    return (value || '').toLowerCase().replace(/\s+/g, ' ').trim();
  }

  /* -------------------------------------------------------------- Search */

  // Remembers which rows the merchant set to open by default, so clearing
  // the search restores that state rather than collapsing everything.
  function rememberDefaults(question) {
    if (!question.dataset.faqDefaultOpen) {
      question.dataset.faqDefaultOpen = question.open ? 'true' : 'false';
    }
  }

  function applyFilter(query) {
    var lists = document.querySelectorAll('[data-faq-list]');
    var matches = 0;

    lists.forEach(function (list) {
      var visibleInList = 0;

      list.querySelectorAll('[data-faq-category]').forEach(function (category) {
        var visibleInCategory = 0;

        category.querySelectorAll('[data-faq-question]').forEach(function (question) {
          rememberDefaults(question);

          var isMatch = !query || normalize(question.textContent).indexOf(query) !== -1;
          question.hidden = !isMatch;

          if (isMatch) {
            visibleInCategory++;
            // Open matches so the answer is visible without another click.
            question.open = query ? true : question.dataset.faqDefaultOpen === 'true';
          }
        });

        category.hidden = visibleInCategory === 0;
        visibleInList += visibleInCategory;
      });

      // Hide the jump link for any category the filter emptied out.
      list.querySelectorAll('[data-faq-nav-target]').forEach(function (pill) {
        var target = document.getElementById(pill.getAttribute('data-faq-nav-target'));
        pill.hidden = !target || target.hidden;
      });

      list.classList.toggle('is-empty', visibleInList === 0);
      var term = list.querySelector('[data-faq-empty-term]');
      if (term) term.textContent = query;
      matches += visibleInList;
    });

    return { matches: matches, lists: lists.length };
  }

  /* ---------------------------------------------------- Category jump links */

  function initNav(nav) {
    if (nav.dataset.faqBound === 'true') return;
    nav.dataset.faqBound = 'true';

    // The category heading is the source of truth for the label. Filling it in
    // here keeps the buttons correct even if the section rendered them empty.
    nav.querySelectorAll('[data-faq-nav-target]').forEach(function (pill) {
      var target = document.getElementById(pill.getAttribute('data-faq-nav-target'));
      var heading = target ? target.querySelector('.sudsy-faq-category__title') : null;

      if (!heading || !heading.textContent.trim()) {
        pill.hidden = true;
        return;
      }
      if (!pill.textContent.trim()) pill.textContent = heading.textContent.trim();
    });

    nav.addEventListener('click', function (event) {
      var pill = event.target.closest('[data-faq-nav-target]');
      if (!pill || !nav.contains(pill)) return;

      var target = document.getElementById(pill.getAttribute('data-faq-nav-target'));
      if (!target) return;

      // Scroll to the heading rather than the wrapper so the offset applies.
      var anchor = target.querySelector('.sudsy-faq-category__title') || target;
      anchor.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }

  function initSearch(input) {
    if (input.dataset.faqBound === 'true') return;
    input.dataset.faqBound = 'true';

    var wrapper = input.closest('.sudsy-faq-search');
    var status = document.getElementById(input.getAttribute('aria-describedby') || '');
    var clearButton = wrapper ? wrapper.querySelector('[data-faq-search-clear]') : null;
    var timer;

    function run() {
      var query = normalize(input.value);
      var result = applyFilter(query);

      if (wrapper) wrapper.classList.toggle('has-value', input.value.length > 0);

      if (status) {
        if (!query) {
          status.textContent = '';
        } else if (result.matches === 0) {
          status.textContent = 'No results for "' + input.value.trim() + '".';
        } else {
          status.textContent =
            result.matches + (result.matches === 1 ? ' result' : ' results') + ' for "' + input.value.trim() + '".';
        }
      }
    }

    input.addEventListener('input', function () {
      clearTimeout(timer);
      timer = setTimeout(run, 150);
    });

    // Enter should not reload the page; jump to the results instead.
    input.addEventListener('keydown', function (event) {
      if (event.key !== 'Enter') return;
      event.preventDefault();
      clearTimeout(timer);
      run();
      var list = document.querySelector('[data-faq-list]');
      if (list) list.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });

    var form = input.closest('form');
    if (form) form.addEventListener('submit', function (event) { event.preventDefault(); });

    if (clearButton) {
      clearButton.addEventListener('click', function () {
        input.value = '';
        run();
        input.focus();
      });
    }

    run();
  }

  /* --------------------------------------------------------------- Modal */

  function slugify(value) {
    return (value || '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  // Resolves a question button to the matching row in the FAQ list, by its
  // explicit key first and then by the question wording.
  function findQuestion(key) {
    if (!key) return null;
    var wanted = slugify(key);
    var all = document.querySelectorAll('[data-faq-question]');
    var i;

    for (i = 0; i < all.length; i++) {
      if (slugify(all[i].getAttribute('data-faq-key')) === wanted) return all[i];
    }
    for (i = 0; i < all.length; i++) {
      var text = all[i].querySelector('.sudsy-faq-q__text');
      if (text && slugify(text.textContent) === wanted) return all[i];
    }
    return null;
  }

  function initCards(root) {
    var dialog = root.querySelector('[data-faq-modal]');
    if (!dialog || dialog.dataset.faqBound === 'true') return;
    dialog.dataset.faqBound = 'true';

    var body = dialog.querySelector('[data-faq-modal-body]');
    var title = dialog.querySelector('[data-faq-modal-title]');
    var closeButton = dialog.querySelector('[data-faq-modal-close]');

    function show() {
      if (typeof dialog.showModal === 'function') {
        dialog.showModal();
      } else {
        dialog.setAttribute('open', '');
      }
      body.scrollTop = 0;
    }

    function open(card) {
      var template = card.querySelector('template');
      body.innerHTML = '';
      if (template) body.appendChild(template.content.cloneNode(true));
      if (title) title.textContent = card.getAttribute('data-faq-card-title') || '';
      show();
    }

    // Question buttons render nothing of their own; they mirror the answer
    // already written in the FAQ list, so there is only one copy to maintain.
    function openQuestion(button) {
      var question = findQuestion(button.getAttribute('data-faq-qlink'));
      body.innerHTML = '';

      if (question) {
        var answer = question.querySelector('.sudsy-faq-q__answer');
        var text = question.querySelector('.sudsy-faq-q__text');
        if (answer) body.innerHTML = answer.innerHTML;
        if (title) {
          title.textContent = text
            ? text.textContent.trim()
            : button.getAttribute('data-faq-qlink-label') || '';
        }
      } else if (title) {
        // No match: still open, showing the button's own label.
        title.textContent = button.getAttribute('data-faq-qlink-label') || '';
      }

      show();
    }

    root.querySelectorAll('[data-faq-qlink]').forEach(function (button) {
      button.addEventListener('click', function () { openQuestion(button); });
    });

    function close() {
      if (typeof dialog.close === 'function') {
        dialog.close();
      } else {
        dialog.removeAttribute('open');
      }
    }

    root.querySelectorAll('[data-faq-card]').forEach(function (card) {
      card.addEventListener('click', function () { open(card); });
    });

    if (closeButton) closeButton.addEventListener('click', close);

    // Clicking the backdrop registers as a click on the dialog itself, because
    // the inner wrapper covers the whole box. Escape is handled natively.
    dialog.addEventListener('click', function (event) {
      if (event.target === dialog) close();
    });

    // Emptying on close stops any video that was still playing.
    dialog.addEventListener('close', function () { body.innerHTML = ''; });
  }

  /* --------------------------------------------------------- Scroll reveal */

  // .sudsy-reveal starts at opacity 0 and is only revealed by an observer that
  // lives in the How it works section. That section isn't on this page, so the
  // FAQ page runs its own. Adding .visible twice is harmless on pages with both.
  var revealObserver = null;

  function initReveal(root) {
    if (window.Shopify && window.Shopify.designMode) {
      root.querySelectorAll('.sudsy-reveal').forEach(function (el) {
        el.classList.add('visible');
      });
      return;
    }

    if (!('IntersectionObserver' in window)) {
      root.querySelectorAll('.sudsy-reveal').forEach(function (el) {
        el.classList.add('visible');
      });
      return;
    }

    if (!revealObserver) {
      revealObserver = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (!entry.isIntersecting) return;
            entry.target.classList.add('visible');
            revealObserver.unobserve(entry.target);
          });
        },
        { threshold: 0.1, rootMargin: '0px 0px -50px 0px' }
      );
    }

    root.querySelectorAll('.sudsy-reveal:not(.visible)').forEach(function (el) {
      revealObserver.observe(el);
    });
  }

  /* ---------------------------------------------------------------- Boot */

  function init(scope) {
    var root = scope || document;
    initReveal(root);
    root.querySelectorAll('[data-faq-nav]').forEach(initNav);
    root.querySelectorAll('[data-faq-search]').forEach(initSearch);
    root.querySelectorAll('[data-faq-cards]').forEach(initCards);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); });
  } else {
    init();
  }

  // Re-bind sections the theme editor re-renders.
  document.addEventListener('shopify:section:load', function (event) { init(event.target); });
})();
